/**
 * DidRoutes — assign/remove Twilio DIDs (V048). Rendered inline under a
 * roster row's "Manage" panel, alongside the rate card. A DID links a Twilio
 * phone number to this agent+tenant so inbound calls route to the right
 * voice-agent context.
 *
 * Reads:  GET    /admin/did-routes?tenant_id=…
 * Writes: POST   /admin/did-routes
 *         DELETE /admin/did-routes/{id}
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { didRoutesService } from "@re-platform/api-client";
import type { AgentDID, StaffMember } from "@re-platform/api-client";
import { PhoneInput } from "@re-platform/ui";
import { api } from "../../lib/api";
import { formatPhone } from "../../lib/contacts";

const field: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 13,
};

const muted: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 13,
};

const danger: React.CSSProperties = {
  color: "var(--rp-color-danger)",
  fontSize: 13,
};

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

export function DidRoutes({
  member,
  tenantId,
}: {
  member: StaffMember;
  /** Selected tenant (super_admin picker) — undefined for a brokerage_admin,
   * whose own tenant is implied server-side. */
  tenantId?: string;
}) {
  const queryClient = useQueryClient();
  const [did, setDid] = useState("");
  const [displayText, setDisplayText] = useState("");
  const queryKey = ["did-routes", tenantId ?? null];

  const routes = useQuery<AgentDID[]>({
    queryKey,
    queryFn: () => didRoutesService.list(api, tenantId),
    staleTime: 15_000,
  });

  // This tenant's route for the agent, plus any their OTHER brokerage assigned
  // (server marks those `foreign`). A dual-licensed agent has a separate agent
  // row per brokerage, so matching on agent_id alone showed "No Twilio number
  // assigned yet" for someone who demonstrably had one — and the number then
  // refused to be assigned here, because a DID routes to exactly one place.
  const mine = (routes.data ?? []).filter((r) =>
    r.foreign
      ? // Foreign rows carry the OTHER brokerage's agent_id/user_id, so they are
        // matched on the local user the server resolved by phone.
        r.matched_user_id === member.id
      : r.agent_id === member.agent_id,
  );
  const hasForeign = mine.some((r) => r.foreign);
  const hasOwn = mine.some((r) => !r.foreign);

  const create = useMutation({
    mutationFn: () =>
      didRoutesService.create(
        api,
        did.trim(),
        { agentId: member.agent_id ?? undefined, tenantId },
        undefined,
        displayText.trim() || undefined,
      ),
    onSuccess: () => {
      setDid("");
      setDisplayText("");
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const remove = useMutation({
    mutationFn: (didId: string) => didRoutesService.remove(api, didId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  if (member.agent_id == null) {
    return (
      <p style={{ ...muted, margin: 0 }}>
        No agent profile on file yet — a DID can't be assigned until one exists.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {routes.isLoading ? (
        <p style={{ ...muted, margin: 0 }}>Loading…</p>
      ) : routes.isError ? (
        <p style={{ ...danger, margin: 0 }}>
          {errMsg(routes.error, "Could not load DIDs")}
        </p>
      ) : mine.length === 0 ? (
        <p style={{ ...muted, margin: 0 }}>No Twilio number assigned yet.</p>
      ) : (
        mine.map((r) => (
          <div
            key={r.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              padding: "8px 10px",
              border: "1px solid var(--rp-color-border)",
              borderRadius: "var(--rp-radius-md)",
              background: "var(--rp-color-surface)",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 13.5 }}>
              {formatPhone(r.did)}
            </span>
            {/* The vanity spelling is what a client actually sees, so show it
                as the number rather than as a footnote about it. */}
            {r.display_text && (
              <span style={{ ...muted, fontSize: 12.5 }}>
                shows as {r.display_text}
              </span>
            )}
            {r.label && (
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--rp-color-surface-2)",
                  color: "var(--rp-color-text-muted)",
                }}
              >
                {r.label}
              </span>
            )}
            <span style={{ flex: 1 }} />
            {r.foreign ? (
              // Shown so this brokerage knows the agent is reachable, but the
              // brokerage that pays for the number is the one that may remove it.
              <span style={{ ...muted, fontSize: 12 }}>
                Managed by {r.owner_brokerage_name ?? "their other brokerage"}
              </span>
            ) : (
              <button
                type="button"
                className="rp-btn rp-btn--secondary rp-btn--sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(r.id)}
              >
                {remove.isPending ? "…" : "Remove"}
              </button>
            )}
          </div>
        ))
      )}

      {/* Say what the situation IS before they try. Typing the agent's existing
          number here is the obvious move and it can only ever fail — one number
          routes to one place — so the useful thing is to explain that inbound
          calls on it already answer for this brokerage too. */}
      {hasForeign && !hasOwn && (
        <p style={{ ...muted, margin: 0 }}>
          Calls to the number above already reach this agent and are answered
          for this brokerage. Assign a number here only if you want a separate
          one for {member.full_name ?? "this agent"} at this brokerage.
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (did.trim() && !create.isPending) create.mutate();
        }}
        // Without the wrap the two inputs and the button ran off the side of
        // the panel on a narrow window, taking "Assign DID" out of reach.
        // minWidth lets each input shrink before the row breaks.
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <PhoneInput
          className=""
          value={did}
          onChange={(e164) => setDid(e164 ?? "")}
          style={{ ...field, width: 180, minWidth: 120, flex: "1 1 auto" }}
        />
        <input
          value={displayText}
          placeholder="Vanity display (optional)"
          onChange={(e) => setDisplayText(e.target.value)}
          style={{ ...field, width: 180, minWidth: 120, flex: "1 1 auto" }}
        />
        <button
          type="submit"
          className="rp-btn rp-btn--primary rp-btn--sm"
          disabled={!did.trim() || create.isPending}
        >
          {create.isPending ? "Assigning…" : "Assign DID"}
        </button>
        {create.isError && (
          <span style={{ ...danger, fontSize: 12 }}>
            {errMsg(create.error, "Could not assign DID")}
          </span>
        )}
      </form>
      <p style={{ ...muted, fontSize: 11.5, margin: 0 }}>
        In the Twilio Console, point this number's "A call comes in" webhook at{" "}
        <code>https://api.newbrainllc.us/twilio/voice</code> (HTTP POST).
      </p>
    </div>
  );
}
